import { useEffect, useState } from 'react';

import { api } from '../api/client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface InventoryValuationItem {
  sku: string;
  name: string;
  quantity: number;
  unit_cost: number;
  total_value: number;
  category?: string;
}

interface InventoryValuation {
  items: InventoryValuationItem[];
  total_value: number;
  as_of: string;
  currency: string;
}

export function InventoryValuationPage() {
  const [valuation, setValuation] = useState<InventoryValuation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await api.reports.getInventoryValuation();
        setValuation(data);
      } catch (err) {
        console.error('Failed to fetch inventory valuation:', err);
        setError('Unable to load inventory valuation');
      } finally {
        setLoading(false);
      }
    }
    void fetchData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading inventory valuation...</p>
      </div>
    );
  }

  if (error || !valuation) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <i className="bi bi-exclamation-triangle-fill text-4xl text-red-500 mb-4"></i>
        <p className="text-muted-foreground">{error ?? 'Unable to load inventory valuation'}</p>
      </div>
    );
  }

  // Group by category
  const groupedItems = valuation.items.reduce((acc, item) => {
    const category = item.category ?? 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, InventoryValuationItem[]>);

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Inventory Valuation</h1>
          <p className="text-sm text-muted-foreground">
            As of {valuation.as_of ?? 'Today'}
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm">
            <i className="bi bi-download mr-2"></i>
            Export CSV
          </Button>
          <Button variant="default" size="sm">
            <i className="bi bi-printer mr-2"></i>
            Print
          </Button>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="bg-muted/50 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Inventory Value</p>
              <p className="text-3xl font-bold font-mono text-primary">
                {formatCurrency(valuation.total_value)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total Items</p>
              <p className="text-2xl font-bold">{valuation.items.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Valuation by Category */}
      {Object.entries(groupedItems).map(([category, items]) => {
        const categoryValue = items.reduce((sum, item) => sum + item.total_value, 0);
        
        return (
          <Card key={category}>
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <CardTitle className="flex items-center gap-2">
                  <i className="bi bi-folder text-muted-foreground"></i>
                  {category}
                </CardTitle>
                <Badge variant="secondary" className="font-mono">
                  {formatCurrency(categoryValue)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">SKU</th>
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Name</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Qty</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Unit Cost</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Total Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.sku} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{item.sku}</code>
                        </td>
                        <td className="py-2 px-3">{item.name}</td>
                        <td className="py-2 px-3 text-right font-mono">{item.quantity}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(item.unit_cost)}</td>
                        <td className="py-2 px-3 text-right font-mono font-semibold">
                          {formatCurrency(item.total_value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Empty State */}
      {valuation.items.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <i className="bi bi-box-seam text-4xl text-muted-foreground mb-4"></i>
            <p className="text-muted-foreground">No inventory items found</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}